import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { requireAuthenticatedUser } from '../../decorators'
import {
  getCaseDataById,
  getCaseListData,
  getCaseOverviewData,
} from './mockData'
import { getCaseNoteListData } from '../caseNotes/mockData'
import {
  getCaseDocumentListData,
  getCaseDocumentListDataByCaseId,
} from '../caseDocuments/mockData'
import {
  encodedReplySize,
  isBlockedAttachmentFilename,
  pickedFiles,
  POSTMARK_MAX_MESSAGE_BYTES,
} from '~/cynqueModules/caseReplyAttachments'
import {
  appendOutboundReplyEmail,
  findCaseEmailSuppression,
  suppressedRecipientsBody,
  moveCaseEmailsToCase,
  getLatestInboundCaseEmail,
  getCaseEmailDataById,
  caseReplyTargetRefusal,
  markCaseEmailReplied,
} from '../caseEmails/mockData'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { getNextVersionNumberString } from '../../modules/getNextVersionNumberString'
import { isStaleVersion } from '../../modules/versionConflict'
import { CaseStatusIds } from '~/services/selectLists/caseStatii'
import type { THrid } from '~/types/models'
import { CaseSource } from '@/types/case/models'
import { subjectForReply } from '~/cynqueModules/subjectForReply'
import {
  isBareEmailAddress,
  duplicateRecipients,
  subjectProblem,
  MAX_RECIPIENTS,
} from '~/cynqueModules/emailRecipientRules'

// CaseReplyForPost's declared fields; anything else is refused (extra="forbid").
const REPLY_BODY_KEYS = [
  'note',
  'subject',
  'to',
  'cc',
  'bcc',
  'documentIds',
  'sendToSuppressed',
  // cynque-api-server #1814 -- ahead of the server; see emailReadStatusRules.ts.
  'inReplyToEmailId',
]
import type { ICase, ICaseReplyForPost } from '@/types/case/models'
import type { ICaseNote } from '~/types/note/models'
import type { ICaseDocument } from '~/types/document/models'

interface GetCasesResponse {
  data: ICase[]
}

async function getCases(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  console.log(`API handler ${getCases.name} invoked.`)
  const responseBody: GetCasesResponse = { data: await getCaseListData() }
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getCaseOverview_byId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const data = await getCaseOverviewData(id)
  if (!data) {
    return res(
      ctx.status(404),
      ctx.json({ statusCode: 404, message: `No case '${id}'.` })
    )
  }
  console.log(`Returning overview for case "${id}".`)
  return res(ctx.status(200), ctx.json({ data }))
}

/**
 * POST /api/cases/:id/replies -- a case's Send Email; an action, not a CRUD
 * create. Mirrors cynque-api-server #1666 (PR #1672):
 *
 *  - The model is `extra="forbid"`: an undeclared key is a 400, never ignored.
 *  - `to` omitted -> whoever last wrote in (the latest inbound's Reply-To, else
 *    From); a case nobody has emailed then 400s. `to` supplied -> used as sent,
 *    and such a case becomes sendable.
 *  - `subject` omitted -> "Re: <descr>" (bare descr with no inbound); supplied
 *    -> verbatim.
 *  - 400 on: an address that is not bare, one repeated across to/cc/bcc
 *    (case-insensitive, derived To included), over 50 recipients, a blank or
 *    multi-line subject -- the same rules the form applies
 *    (cynqueModules/emailRecipientRules.ts).
 *  - 409 when ANY recipient is suppressed, naming them all (`suppressed`).
 *  - `documentIds` (#1488): 400 unless every id is a document on this case;
 *    413 when they come to more than the mail limit once encoded.
 *
 * And #1674 (PR #1678), which made the route MULTIPART-ONLY:
 *
 *  - 415 for anything else, JSON included -- so a client still sending JSON
 *    fails here exactly as it does against the server.
 *  - The reply is the `payload` part, as JSON (a missing or unparseable one is
 *    a 400); each new file is a `files` part, the name repeated.
 *  - An empty file, or a type the mail provider blocks, is a 400 naming the
 *    files (`filenames`) -- for new files straight after the payload is read,
 *    for `documentIds` after suppression; the 413 counts both.
 *  - Each new file becomes a case document BEFORE the message, in the order
 *    sent. The response is the message alone.
 *
 * ⚠ A 400 is what the real app maps every body-validation failure to
 * (`validation_handler`); an earlier draft of #1666 said 422.
 *
 * ⚠ Nothing is sent. The server writes a `queued` message, its `body` the text
 * the user wrote, and returns it (#1737 -- it used to write and return a
 * CaseNote); a worker does the sending. So the mock writes an outbound email in
 * `queued`, and never moves it on -- a 201 means recorded, not delivered.
 */
const postCaseReply = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  if (typeof id !== 'string') {
    return res(
      ctx.status(500),
      ctx.json({ statusCode: 500, message: 'Passed ID is not a string.' })
    )
  }
  const contentType = req.headers.get('content-type') || ''
  if (!contentType.includes('multipart/form-data')) {
    return res(
      ctx.status(415),
      ctx.json({
        status: 415,
        title: 'Unsupported media type',
        detail:
          `Replies are multipart/form-data, not ${
            contentType.split(';')[0] || 'an empty body'
          }: a 'payload' part carrying the reply as JSON, and a 'files' part ` +
          'per attachment.',
      })
    )
  }
  const refuse = (detail: string) =>
    res(
      ctx.status(400),
      ctx.json({ status: 400, title: 'Validation failed', detail })
    )
  const parts = (req.body ?? {}) as Record<string, unknown>
  let body: ICaseReplyForPost
  try {
    body = JSON.parse(String(parts.payload)) as ICaseReplyForPost
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      throw new TypeError('payload is not an object')
    }
  } catch {
    return refuse("The multipart 'payload' part is not a valid reply.")
  }
  const files = pickedFiles(parts.files)
  // The attachments refusal the server raises: worded for the user, naming the
  // files, with the names again in `filenames`.
  const refuseFiles = (title: string, detail: string, filenames: string[]) =>
    res(
      ctx.status(400),
      ctx.json({ status: 400, title, detail, extra: { filenames } })
    )
  const unknownKeys = Object.keys(body).filter(
    (key) => !REPLY_BODY_KEYS.includes(key)
  )
  if (unknownKeys.length) {
    return refuse(`Unknown field(s): ${unknownKeys.join(', ')}.`)
  }
  const cc = body.cc ?? []
  const bcc = body.bcc ?? []
  if (body.to !== undefined && body.to.length === 0) {
    return refuse('to, when supplied, must name at least one address.')
  }
  const badAddress = [...(body.to ?? []), ...cc, ...bcc].find(
    (address) => !isBareEmailAddress(address)
  )
  if (badAddress !== undefined) {
    return refuse(`'${badAddress}' is not a bare email address (local@domain).`)
  }
  if (body.subject !== undefined) {
    const problem = subjectProblem(body.subject)
    if (problem !== true) {
      return refuse(problem)
    }
  }
  // New files are read and checked with the payload, before anything about
  // the case -- the server's `_read_files`.
  const emptyFiles = files.filter((f) => f.size === 0).map((f) => f.name)
  if (emptyFiles.length) {
    return refuseFiles(
      'Empty attachment',
      `These files have no content: ${emptyFiles.join(', ')}.`,
      emptyFiles
    )
  }
  const blockedFiles = files
    .map((f) => f.name)
    .filter(isBlockedAttachmentFilename)
  if (blockedFiles.length) {
    return refuseFiles(
      'Attachment type not allowed',
      'Postmark refuses these file types, so this reply could never be ' +
        `sent: ${blockedFiles.join(', ')}. Send them another way, or zip them.`,
      blockedFiles
    )
  }
  // Threading, and the derived To: the latest inbound message, when there is
  // one. A case opened in the app has none -- its email starts a new
  // conversation, and it must say who it is to.
  //
  // `inReplyToEmailId` (cynque-api-server #1814) names the message instead: an
  // inbound email on THIS case, or a 400. The reply threads onto it, and it is
  // the one marked Replied below.
  if (body.inReplyToEmailId !== undefined) {
    const refusal = await caseReplyTargetRefusal(id, body.inReplyToEmailId)
    if (refusal) {
      return refuse(refusal)
    }
  }
  const inbound =
    body.inReplyToEmailId !== undefined
      ? await getCaseEmailDataById(body.inReplyToEmailId)
      : await getLatestInboundCaseEmail(id)
  const derivedTo = inbound?.replyTo || inbound?.fromAddress
  const to = body.to ?? (derivedTo ? [derivedTo] : [])
  if (to.length === 0) {
    return res(
      ctx.status(400),
      ctx.json({
        status: 400,
        title: 'Nothing to reply to',
        detail: 'This case has no inbound email, so to must name a recipient.',
      })
    )
  }
  const duplicates = duplicateRecipients(to, cc, bcc)
  if (duplicates.length) {
    return refuse(
      `Each recipient may appear once across to, cc and bcc: ${duplicates.join(
        ', '
      )}.`
    )
  }
  const total = to.length + cc.length + bcc.length
  if (total > MAX_RECIPIENTS) {
    return refuse(
      `${total} recipients; Postmark accepts at most ${MAX_RECIPIENTS}.`
    )
  }
  // The suppression check happens BEFORE anything is written, exactly as the
  // server does it -- so a user is told before spending effort on a reply rather
  // than after, and nothing has to be undone. `sendToSuppressed` proceeds
  // knowingly; it does NOT deliver, and the row it records fails visibly.
  if (!body.sendToSuppressed) {
    const suppressions = [...to, ...cc, ...bcc]
      .map((address) => findCaseEmailSuppression(address))
      .filter((found): found is NonNullable<typeof found> => !!found)
    const first = suppressions[0]
    if (first) {
      return res(
        ctx.status(409),
        ctx.json(suppressedRecipientsBody(suppressions))
      )
    }
  }
  // Attachments, after suppression and before anything is written -- the
  // server's order (`_resolve_attachments`). Every id must be a document on
  // THIS case (400, deliberately not saying which), none may be a blocked type,
  // and the encoded total -- documents AND new files -- must fit the mail
  // provider's limit (413).
  const documentIds = [...new Set(body.documentIds ?? [])]
  const found = documentIds.length
    ? (await getCaseDocumentListDataByCaseId(id)).filter((d) =>
        documentIds.includes(d.id)
      )
    : []
  if (found.length !== documentIds.length) {
    return res(
      ctx.status(400),
      ctx.json({
        status: 400,
        title: 'Unknown document',
        detail:
          'Every documentId must be a document on this case. ' +
          `${found.length} of ${documentIds.length} matched.`,
      })
    )
  }
  const blockedDocuments = found
    .map((d) => d.filename)
    .filter(isBlockedAttachmentFilename)
  if (blockedDocuments.length) {
    return refuseFiles(
      'Attachment type not allowed',
      'Postmark refuses these file types, so this reply could never be ' +
        `sent: ${blockedDocuments.join(', ')}. Send them another way, or ` +
        'zip them.',
      blockedDocuments
    )
  }
  const attachmentBytes = [
    ...found.map((d) => d.filesize),
    ...files.map((f) => f.size),
  ]
  const size = encodedReplySize({ attachmentBytes, body: body.note ?? '' })
  if (size > POSTMARK_MAX_MESSAGE_BYTES) {
    const raw = attachmentBytes.reduce((sum, bytes) => sum + bytes, 0)
    const mb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1)
    return res(
      ctx.status(413),
      ctx.json({
        status: 413,
        title: 'Reply is too large to send',
        detail:
          `${attachmentBytes.length} attachment(s) come to ${mb(raw)} MB, ` +
          `which is about ${mb(Math.floor((raw * 4) / 3))} MB once encoded ` +
          `for email -- over the ${
            POSTMARK_MAX_MESSAGE_BYTES / 1024 / 1024
          } MB limit. Send fewer files, or share them another way.`,
      })
    )
  }
  const now = new Date().toISOString()
  // The new files first, as case documents in the order sent: the server
  // stores them before the message, in the same transaction, so they exist only
  // if the reply does. (The bytes aren't kept -- see parsePostedEntity.)
  if (files.length) {
    const documents = await getCaseDocumentListData()
    const descr = (await getCaseDataById(id))?.descr ?? ''
    let next =
      Math.max(
        0,
        ...documents.map((d) => Number(String(d.hrid).replace(/\D/g, '')) || 0)
      ) + 1
    for (const file of files) {
      const serial = String(next++).padStart(4, '0')
      const documentId = `XXCAD${serial}`
      documents.push({
        id: documentId,
        hrid: `CAD${serial}`,
        case: { id, label: descr },
        descr: file.name,
        filename: file.name,
        filetype: file.type || 'application/octet-stream',
        filesize: file.size,
        fileChecksumAlgorithm: 'sha256',
        uploadedByTeamMember: { id: 'XXTM0006', label: 'Service Technician' },
        // ⚠ The `?sig=` is load-bearing -- see parsePostedEntity.
        fileUrlPath: `/mock-files/${documentId}/${file.name}?sig=mock`,
        caseEmailId: null,
        contentId: null,
        createdAt: now,
        modifiedAt: now,
        version: '1',
        canBeDeleted: true,
      } as unknown as ICaseDocument)
    }
    await _storeEntityDataListToLocalForage({
      entityKeyBase: 'casedocuments',
      listData: documents,
      callingFn: '[api/msw/api/cases/index.ts] postCaseReply |',
    })
  }
  const descr = (await getCaseDataById(id))?.descr ?? ''
  // The message alone: since cynque-api-server #1737 a reply writes no note,
  // and returns the queued message as the overview's `caseEmails` carries it.
  const caseEmail = await appendOutboundReplyEmail({
    caseId: id,
    body: body.note,
    // The agent composed this, so the agent sent it.
    sentBy: { id: 'XXTM0006', label: 'Service Technician' },
    // Verbatim when sent; otherwise the server's own wording.
    subject: body.subject ?? (inbound ? subjectForReply(descr) : descr.trim()),
    toAddresses: to,
    ccAddresses: cc,
    bccAddresses: bcc,
    inbound,
  })
  // The message answered becomes Replied, in the same request: the one named,
  // or the one the reply threaded onto (cynque-api-server #1814).
  if (inbound) {
    await markCaseEmailReplied(inbound.id)
  }
  return res(ctx.status(201), ctx.json({ data: caseEmail }))
}

/** GET /api/cases/:id -- the server's generated get-by-id (make_request_handlers). */
async function getCase_byId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  const found = typeof id === 'string' ? await getCaseDataById(id) : undefined
  if (!found) {
    return res(
      ctx.status(404),
      ctx.json({
        status: 404,
        title: 'Entity not found',
        detail: `Case=${id} not found.`,
      })
    )
  }
  return res(ctx.status(200), ctx.json({ data: found }))
}

/**
 * POST /api/cases/:id/merges -- fold `source` INTO the path case (the survivor).
 * Mirrors cynque-api-server #1658 / PR #1679, in its order:
 *  - 400: a malformed body, or a self-merge;
 *  - 404: either case missing;
 *  - 409 "Case already merged": either side already merged;
 *  - 409 "Case version conflict": either version stale;
 *  - then the source's notes, emails and documents move to the survivor, the
 *    source becomes status Merged with `mergedInto` the survivor, a note is
 *    written on each case, and the survivor is returned (200).
 *
 * ⚠ NARROWER than the server in one way: it does not move the source's
 * activities or company/project/supplier/TSD links -- the mock keeps none
 * per case. Nothing in the UI reads those across a merge.
 */
const postCaseMerge = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  const body = (await req.json()) as {
    version?: string
    source?: { id?: string; version?: string }
  }
  const problem = (status: number, title: string, detail: string) =>
    res(ctx.status(status), ctx.json({ status, title, detail }))
  if (
    typeof id !== 'string' ||
    !body?.version ||
    !body.source?.id ||
    !body.source.version
  ) {
    return problem(
      400,
      'Validation failed',
      'version and source { id, version } are required.'
    )
  }
  if (body.source.id === id) {
    return problem(
      400,
      'Cannot merge a case into itself',
      `source.id and the path id are both ${id}.`
    )
  }
  const survivor = await getCaseDataById(id)
  if (!survivor) {
    return problem(404, 'Survivor case not found', `Case=${id} not found.`)
  }
  const source = await getCaseDataById(body.source.id)
  if (!source) {
    return problem(
      404,
      'Source case not found',
      `Case=${body.source.id} not found.`
    )
  }
  for (const [theCase, role] of [
    [survivor, 'survivor'],
    [source, 'source'],
  ] as const) {
    if (theCase.mergedInto) {
      return problem(
        409,
        'Case already merged',
        `The ${role} Case=${theCase.id} was merged into Case=${theCase.mergedInto}.`
      )
    }
  }
  if (
    isStaleVersion(body.version, survivor.version) ||
    isStaleVersion(body.source.version, source.version)
  ) {
    return problem(
      409,
      'Case version conflict',
      'A case was modified concurrently; re-read both and retry.'
    )
  }

  const survivorRef = { id: survivor.id, label: survivor.descr }
  const fn = '[api/msw/api/cases/index.ts] postCaseMerge |'

  const notes = await getCaseNoteListData()
  for (const note of notes) {
    if (note.case?.id === source.id) {
      note.case = { ...survivorRef }
    }
  }
  const documents = await getCaseDocumentListData()
  for (const document of documents) {
    if (document.case?.id === source.id) {
      document.case = { ...survivorRef }
    }
  }
  await moveCaseEmailsToCase({ fromCaseId: source.id, toCaseId: survivor.id })

  const now = new Date().toISOString()
  const mergeNote = (theCase: ICase, text: string) => {
    const serial = String(notes.length + 1).padStart(4, '0')
    notes.push({
      id: `XXCAN${serial}`,
      hrid: `CAN${serial}`,
      case: { id: theCase.id, label: theCase.descr },
      note: text,
      enteredByTeamMember: { id: 'XXTM0006', label: 'Service Technician' },
      status: { id: 1, label: 'Open' },
      createdAt: now,
      modifiedAt: now,
      version: '1',
      canBeDeleted: true,
    } as unknown as ICaseNote)
  }
  mergeNote(survivor, `Merged ${source.hrid} (${source.descr}) into this case.`)
  mergeNote(source, `Merged into ${survivor.hrid} (${survivor.descr}).`)

  Object.assign(source, {
    status: { id: CaseStatusIds.MERGED, label: 'Merged' },
    mergedInto: survivor.id,
    version: getNextVersionNumberString(source.version),
    modifiedAt: now,
  })
  Object.assign(survivor, {
    version: getNextVersionNumberString(survivor.version),
    modifiedAt: now,
  })

  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'casenotes',
    listData: notes,
    callingFn: fn,
  })
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'casedocuments',
    listData: documents,
    callingFn: fn,
  })
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'cases',
    listData: await getCaseListData(),
    callingFn: fn,
  })
  return res(ctx.status(200), ctx.json({ data: survivor }))
}

// post

interface PostCaseResponse {
  hrid: THrid
}

// `source` is SERVER-MAINTAINED (issue #1635). Its column is
// `insert_column=False` with a `'manual'` default, so the webapp never sends it
// and the api stamps every case it creates. Mirrored here because the mock is
// otherwise NARROWER than the server: a case created under MSW came back with no
// `source` at all, while the same create against the real API comes back
// `'manual'` -- so any code branching on it would read `undefined` in dev and a
// string in production. Only the email-to-case pathway, which has no webapp
// POST, produces `'email'`; nothing reachable from here can.
const stampManualSource = async ({ targetItem }: { targetItem: ICase }) => {
  targetItem.source = CaseSource.MANUAL
}

/**
 * A case names its company OR its companyMSService, never both
 * (cynque-api-server #1750), and the mock refuses what the server refuses --
 * otherwise a client that sent both would pass every test here:
 *  - POST naming both: 400 (CaseModel's validator);
 *  - PATCH setting a company while a service stays: 409, the UpdateConflict
 *    the CHECK `cases_company_or_service_check` raises;
 *  - PATCH attaching a service: the company is nulled (the server's BEFORE
 *    UPDATE trigger), even when the PATCH names a company too.
 */
const COMPANY_OR_SERVICE_DETAIL =
  'A case names its company or its companyMSService, not both.'

function namesBoth(theCase: Partial<ICase>) {
  return !!theCase.company?.id && !!theCase.companyMSService?.id
}

const postCase = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const posted = (await req.json()) as Partial<ICase>
  if (namesBoth(posted)) {
    return res(
      ctx.status(400),
      ctx.json({
        status: 400,
        title: 'Bad Request',
        detail: COMPANY_OR_SERVICE_DETAIL,
      })
    )
  }
  return addToEntityListData(req, res, ctx, getCaseListData, stampManualSource)
}

const patchCase = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  const patch = (await req.json()) as Partial<ICase>
  const stored = typeof id === 'string' ? await getCaseDataById(id) : undefined
  // Read NOW: `stored` may be the very row the update assigns into.
  const storedServiceId = stored?.companyMSService?.id
  const attachesService =
    !!patch.companyMSService?.id &&
    patch.companyMSService.id !== storedServiceId
  if (stored) {
    const merged = {
      ...stored,
      ...patch,
      ...(attachesService ? { company: null } : {}),
    }
    if (namesBoth(merged)) {
      return res(
        ctx.status(409),
        ctx.json({
          status: 409,
          title: 'Update conflict',
          detail:
            'new row for relation "cases" violates check constraint' +
            ' "cases_company_or_service_check"',
        })
      )
    }
  }
  return updateEntityListData(req, res, ctx, getCaseListData, [
    // The server's trigger: attaching a service supersedes the company.
    async ({ targetItem }: { targetItem: ICase }) => {
      if (attachesService) {
        targetItem.company = null
      }
    },
  ])
}

const deleteCase = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getCaseListData)
}

export const handlers = [
  {
    method: 'get',
    path: '/api/cases',
    handler: getCases,
  },
  {
    method: 'get',
    path: '/api/cases/:id/overview',
    handler: getCaseOverview_byId,
  },
  {
    method: 'post',
    path: '/api/cases/:id/replies',
    handler: postCaseReply,
  },
  {
    method: 'post',
    path: '/api/cases/:id/merges',
    handler: postCaseMerge,
  },
  {
    method: 'get',
    path: '/api/cases/:id',
    handler: getCase_byId,
  },
  {
    method: 'post',
    path: '/api/cases',
    handler: postCase,
  },
  {
    method: 'patch',
    path: '/api/cases/:id',
    handler: patchCase,
  },
  {
    method: 'delete',
    path: '/api/cases/:id',
    handler: deleteCase,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
